import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const SAILINGS_API = `${API_BASE}/sailings`;
const BOOKINGS_API = `${API_BASE}/bookings`;

const Dashboard = () => {
  const [sailings, setSailings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [sailingRes, bookingRes] = await Promise.all([
        fetch(SAILINGS_API),
        fetch(BOOKINGS_API),
      ]);
      const sailingsData = await sailingRes.json();
      const bookingsData = await bookingRes.json();
      setSailings(Array.isArray(sailingsData) ? sailingsData : (sailingsData?.data || []));
      setBookings(Array.isArray(bookingsData) ? bookingsData : (bookingsData?.data || []));
    } catch (error) {
      console.error("Failed to load dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const linkedBookingsForSailing = (sailing) =>
    bookings.filter((b) => {
      const ref = sailing.sailingRef;
      return (
        String(b.sailing) === String(sailing._id) ||
        String(b.sailingId) === String(sailing._id) ||
        b.requestedSailingRef === ref ||
        b.sailingSnapshot?.sailingRef === ref
      );
    });

  const calcStats = (sailing) => {
    const linked = linkedBookingsForSailing(sailing);
    // "completed" is the natural end state after invoiced (see BookingDetail's
    // workflow) — excluding it here made a fully Arrived/completed sailing's
    // capacity and revenue read as 0% even though it actually sailed full.
    const confirmed = linked.filter(b => ["confirmed","invoiced","completed"].includes(String(b.status).toLowerCase()));
    const livestockHead = confirmed.reduce((s, b) => s + (b.livestockItems||[]).reduce((ss,i) => ss + Number(i.headCount||0), 0), 0);
    const weightKg = confirmed.reduce((s, b) => s + (b.livestockItems||[]).reduce((ss,i) => ss + (Number(i.totalWeightKg||0) || Number(i.headCount||0)*Number(i.averageWeightKg||0)), 0) + (b.freightItems||[]).reduce((ss,i) => ss + Number(i.weightKg||0), 0), 0);
    const cubeM3 = confirmed.reduce((s, b) => s + (b.freightItems||[]).reduce((ss,i) => ss + Number(i.cubeM3||0), 0), 0);
    const revenue = confirmed.reduce((s, b) => s + getBookingRevenue(b), 0);
    // Capacity actually lives nested under sailing.capacity.* (see
    // SailingDetail.jsx's DEFAULT_CAPACITY usage) — these flat field names
    // don't exist on the sailing record, so this was always falling back to
    // the hardcoded defaults below regardless of the sailing's real capacity,
    // e.g. showing "328 / 1200 hd" here while SailingDetail correctly showed
    // "328 / 350 hd" for the same voyage.
    const livestockCapacity = Number(sailing.capacity?.maxLivestockHead) || 1200;
    const weightCapacity = Number(sailing.capacity?.maxWeightKg) || 250000;
    const freightCapacity = Number(sailing.capacity?.maxCubeM3) || 300;
    const livestockPct = pct(livestockHead, livestockCapacity);
    const weightPct = pct(weightKg, weightCapacity);
    const freightPct = pct(cubeM3, freightCapacity);
    // A sailing is "full" the moment any one dimension maxes out — averaging
    // livestock/weight/freight % previously let a sailing at 100% weight but
    // 0% livestock/freight read as only ~33%, silently missing capacity alerts.
    const overallPct = Math.max(livestockPct, weightPct, freightPct);
    return { linked, confirmed, livestockHead, weightKg, cubeM3, revenue, livestockCapacity, weightCapacity, freightCapacity, livestockPct, weightPct, freightPct, overallPct };
  };

  // ── KPIs ──
  const pendingBookings = bookings.filter(b => String(b.status).toLowerCase() === "pending").length;
  const confirmedBookings = bookings.filter(b => ["confirmed","invoiced","completed"].includes(String(b.status).toLowerCase()));
  const totalRevenue = confirmedBookings.reduce((s,b) => s + getBookingRevenue(b), 0);
  // Pipeline should only be bookings NOT yet in "Confirmed Revenue" above —
  // it previously also included "confirmed" bookings, double-counting them
  // across both KPI tiles.
  const pendingRevenue = bookings.filter(b => String(b.status).toLowerCase() === "pending").reduce((s,b) => s + getBookingRevenue(b), 0);

  const upcomingSailings = sailings.filter(s => {
    const d = new Date(s.etd);
    return !isNaN(d) && d >= new Date() && !["closed","cancelled"].includes(s.status);
  });

  const activeSailings = sailings.filter(s => ["loading","departed"].includes(s.status));

  const nextSailing = useMemo(() => {
    const now = new Date();
    return [...sailings]
      // Match upcomingSailings' status filter — otherwise a cancelled sailing
      // with a future ETD could still be shown as the hero "Next Sailing" card.
      .filter(s => { const d = new Date(s.etd); return !isNaN(d) && d >= now && !["closed","cancelled"].includes(s.status); })
      .sort((a,b) => new Date(a.etd) - new Date(b.etd))[0];
  }, [sailings]);

  const nextStats = nextSailing ? calcStats(nextSailing) : null;

  const capacityAlerts = sailings.filter(s => {
    const st = calcStats(s);
    return st.overallPct >= 85 && !["closed","arrived"].includes(s.status);
  });

  const topCustomers = Object.values(
    bookings.reduce((acc, b) => {
      const name = b.customerName || "Unknown";
      const rev = getBookingRevenue(b);
      if (!acc[name]) acc[name] = { name, revenue: 0, bookings: 0 };
      acc[name].revenue += rev;
      acc[name].bookings += 1;
      return acc;
    }, {})
  ).sort((a,b) => b.revenue - a.revenue).slice(0, 5);

  const recentBookings = [...bookings]
    .sort((a,b) => new Date(b.createdAt||0) - new Date(a.createdAt||0))
    .slice(0, 6);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const greeting = `Good ${new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}${user.firstname ? `, ${user.firstname}` : ""}`;

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="text-sm text-gray-400">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="w-full p-6 space-y-6">

      {/* Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">{greeting}. Here's what's happening today.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/dashboard/sailings/new" className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">+ New Sailing</Link>
          <Link to="/dashboard/bookings/new" className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-700">+ New Booking</Link>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        <KpiCard label="Upcoming Sailings" value={upcomingSailings.length} to="/dashboard/sailings" />
        <KpiCard label="Active Voyages" value={activeSailings.length} highlight={activeSailings.length > 0 ? "blue" : null} to="/dashboard/sailings" />
        <KpiCard label="Pending Requests" value={pendingBookings} highlight={pendingBookings > 0 ? "amber" : null} to="/dashboard/bookings" />
        <KpiCard label="Capacity Alerts" value={capacityAlerts.length} highlight={capacityAlerts.length > 0 ? "red" : null} />
        <KpiCard label="Confirmed Revenue" value={`$${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
        <KpiCard label="Revenue Pipeline" value={`$${pendingRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
      </div>

      {/* Active sailings banner */}
      {activeSailings.length > 0 && (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 px-5 py-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-blue-500 mb-3">Active Voyages</div>
          <div className="space-y-2">
            {activeSailings.map(s => (
              <div key={s._id} className="flex items-center justify-between">
                <div>
                  <span className="font-semibold text-blue-900 text-sm">{s.sailingRef}</span>
                  <span className="text-blue-700 text-sm ml-2">{s.origin} → {s.destination}</span>
                  <span className={`ml-2 inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${s.status === "loading" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>{s.status}</span>
                </div>
                <Link to={`/dashboard/sailings/${s._id}`} className="text-xs font-semibold text-blue-700 hover:underline">View →</Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main content grid */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_360px]">

        {/* Left column */}
        <div className="space-y-6">

          {/* Next Sailing */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Next Sailing</h2>
              <Link to="/dashboard/sailings" className="text-xs font-semibold text-gray-500 hover:text-gray-900">View all →</Link>
            </div>
            {nextSailing && nextStats ? (
              <div className="p-5 space-y-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <StatusBadge status={nextSailing.status || "open"} />
                      <span className="text-xs text-gray-400">{nextSailing.sailingRef}</span>
                    </div>
                    <div className="text-xl font-bold text-gray-900">{nextSailing.origin} → {nextSailing.destination}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      {nextSailing.vesselName || nextSailing.vessel?.name || "—"} · ETD {fmtDate(nextSailing.etd)}
                    </div>
                  </div>
                  <Link to={`/dashboard/sailings/${nextSailing._id}`} className="shrink-0 rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-700">
                    Open
                  </Link>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <StatTile label="Bookings" value={nextStats.linked.length} />
                  <StatTile label="Livestock" value={`${nextStats.livestockHead} hd`} />
                  <StatTile label="Revenue" value={`$${nextStats.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                </div>

                <div className="space-y-3">
                  <CapBar label="Livestock" pct={nextStats.livestockPct} value={`${nextStats.livestockHead} / ${nextStats.livestockCapacity} hd`} />
                  <CapBar label="Weight" pct={nextStats.weightPct} value={`${(nextStats.weightKg/1000).toFixed(1)} / ${(nextStats.weightCapacity/1000).toFixed(0)} t`} />
                  <CapBar label="Freight" pct={nextStats.freightPct} value={`${nextStats.cubeM3} / ${nextStats.freightCapacity} m³`} />
                </div>
              </div>
            ) : (
              <div className="px-5 py-10 text-center text-sm text-gray-400">
                No upcoming sailings. <Link to="/dashboard/sailings/new" className="font-semibold text-gray-700 hover:underline">Create one →</Link>
              </div>
            )}
          </div>

          {/* Sailings list */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Sailings</h2>
              <Link to="/dashboard/sailings" className="text-xs font-semibold text-gray-500 hover:text-gray-900">View all →</Link>
            </div>
            {sailings.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-gray-400">No sailings yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-semibold text-gray-600">Sailing</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Route</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">ETD</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Bookings</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Capacity</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {sailings.slice(0, 8).map((s, i) => {
                    const st = calcStats(s);
                    return (
                      <tr key={s._id} className={`hover:bg-gray-50 ${i > 0 ? "border-t border-gray-100" : ""}`}>
                        <td className="px-4 py-3 font-medium text-gray-900">{s.sailingRef}</td>
                        <td className="px-4 py-3 text-gray-600">{s.origin} → {s.destination}</td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtDate(s.etd)}</td>
                        <td className="px-4 py-3 text-gray-600">{st.linked.length}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className={`h-1.5 rounded-full ${st.overallPct >= 90 ? "bg-red-500" : st.overallPct >= 75 ? "bg-amber-400" : "bg-emerald-500"}`} style={{width:`${st.overallPct}%`}} />
                            </div>
                            <span className="text-xs text-gray-400 w-8">{st.overallPct}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                        <td className="px-4 py-3 text-right">
                          <Link to={`/dashboard/sailings/${s._id}`} className="text-xs font-semibold text-gray-900 hover:underline">View</Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Recent Bookings */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Recent Bookings</h2>
              <Link to="/dashboard/bookings" className="text-xs font-semibold text-gray-500 hover:text-gray-900">View all →</Link>
            </div>
            {recentBookings.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-gray-400">No bookings yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-semibold text-gray-600">Ref</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Customer</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Type</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Sailing</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {recentBookings.map((b, i) => (
                    <tr key={b._id} className={`hover:bg-gray-50 ${i > 0 ? "border-t border-gray-100" : ""}`}>
                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap text-xs">{b.bookingRef || b._id?.slice(-6).toUpperCase()}</td>
                      <td className="px-4 py-3 text-gray-700">{b.customerName || "—"}</td>
                      <td className="px-4 py-3 text-gray-500 capitalize">{b.bookingType || "—"}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{b.requestedSailingRef || "—"}</td>
                      <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/dashboard/bookings/${b._id}`} className="text-xs font-semibold text-gray-900 hover:underline">View</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6">

          {/* Alerts */}
          {capacityAlerts.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-amber-100 bg-amber-50">
                <h2 className="text-base font-semibold text-amber-800">⚠ Capacity Alerts</h2>
                <p className="text-xs text-amber-600 mt-0.5">{capacityAlerts.length} sailing{capacityAlerts.length !== 1 ? "s" : ""} at 85%+ capacity</p>
              </div>
              <div className="divide-y divide-gray-100">
                {capacityAlerts.map(s => {
                  const st = calcStats(s);
                  return (
                    <div key={s._id} className="px-5 py-3 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{s.sailingRef}</div>
                        <div className="text-xs text-gray-500">{s.origin} → {s.destination} · {st.overallPct}% full</div>
                      </div>
                      <Link to={`/dashboard/sailings/${s._id}`} className="text-xs font-semibold text-amber-700 hover:underline">Open →</Link>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pending requests */}
          {pendingBookings > 0 && (
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">Pending Requests</h2>
                <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700">{pendingBookings}</span>
              </div>
              <div className="divide-y divide-gray-100">
                {bookings.filter(b => String(b.status).toLowerCase() === "pending").slice(0, 5).map(b => (
                  <div key={b._id} className="px-5 py-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{b.customerName || "Unknown"}</div>
                      <div className="text-xs text-gray-400 capitalize">{b.bookingType} · {b.requestedSailingRef || "No sailing"}</div>
                    </div>
                    <Link to={`/dashboard/bookings/${b._id}`} className="text-xs font-semibold text-gray-700 hover:underline">Review →</Link>
                  </div>
                ))}
                {pendingBookings > 5 && (
                  <div className="px-5 py-3">
                    <Link to="/dashboard/bookings" className="text-xs font-semibold text-gray-500 hover:underline">View all {pendingBookings} pending →</Link>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Top Customers */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Top Customers</h2>
              <Link to="/dashboard/customers" className="text-xs font-semibold text-gray-500 hover:text-gray-900">View all →</Link>
            </div>
            {topCustomers.length === 0 ? (
              <div className="px-5 py-8 text-sm text-gray-400 text-center">No customer data yet.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {topCustomers.map((c, i) => (
                  <div key={c.name} className="px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-gray-300 w-4">{i + 1}</span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{c.name}</div>
                        <div className="text-xs text-gray-400">{c.bookings} booking{c.bookings !== 1 ? "s" : ""}</div>
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-gray-900">${c.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              {[
                { to: "/dashboard/sailings/new", label: "+ New Sailing" },
                { to: "/dashboard/bookings/new", label: "+ New Booking" },
                { to: "/dashboard/customers/new", label: "+ New Customer" },
                { to: "/dashboard/livestock", label: "Livestock Overview" },
                { to: "/dashboard/settings/rates", label: "Rate Settings" },
              ].map(link => (
                <Link key={link.to} to={link.to} className="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
                  {link.label}
                  <span className="text-gray-400">→</span>
                </Link>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// ── Helpers ──
// A booking's real revenue: prefer the actual calculated/charged total
// (set once a booking is priced, whether manually via "Calculate Charges"
// or via Stripe checkout — see stripe.route.js) over the older pre-pricing
// estimate fields. Stripe's webhook only ever writes invoice.amountPaid and
// charges.total, never pricingSnapshot/totals — so reading only the estimate
// fields (as this dashboard previously did) left real, paid Stripe revenue
// out of Confirmed Revenue, Revenue Pipeline, and Top Customers entirely.
const getBookingRevenue = (b) =>
  Number(b.charges?.total || b.pricingSnapshot?.calculatedTotal || b.totals?.estimatedRevenue || 0);

const pct = (cur, tot) => !tot ? 0 : Math.min(Math.round((cur / tot) * 100), 100);
const fmtDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-NZ", { day:"2-digit", month:"short", year:"numeric" });
};

// ── Components ──
const KpiCard = ({ label, value, highlight, to }) => {
  const colours = highlight === "amber" ? "border-amber-200 bg-amber-50" : highlight === "red" ? "border-red-200 bg-red-50" : highlight === "blue" ? "border-blue-200 bg-blue-50" : "border-gray-200 bg-white";
  const textColour = highlight === "amber" ? "text-amber-900" : highlight === "red" ? "text-red-900" : highlight === "blue" ? "text-blue-900" : "text-gray-900";
  const labelColour = highlight === "amber" ? "text-amber-600" : highlight === "red" ? "text-red-500" : highlight === "blue" ? "text-blue-600" : "text-gray-500";
  const card = (
    <div className={`rounded-2xl border p-4 shadow-sm ${colours}`}>
      <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${labelColour}`}>{label}</div>
      <div className={`text-2xl font-bold tracking-tight ${textColour}`}>{value}</div>
    </div>
  );
  return to ? <Link to={to}>{card}</Link> : card;
};

const StatTile = ({ label, value }) => (
  <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 text-center">
    <div className="text-xs text-gray-400 mb-0.5">{label}</div>
    <div className="text-sm font-bold text-gray-900">{value}</div>
  </div>
);

const CapBar = ({ label, pct: p, value }) => (
  <div>
    <div className="flex items-center justify-between text-xs mb-1">
      <span className="font-medium text-gray-700">{label}</span>
      <span className="text-gray-400">{value}</span>
    </div>
    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
      <div className={`h-2 rounded-full transition-all ${p >= 90 ? "bg-red-500" : p >= 75 ? "bg-amber-400" : "bg-gray-900"}`} style={{width:`${p}%`}} />
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const v = String(status || "").toLowerCase();
  const s = v === "confirmed" || v === "open" ? "bg-emerald-50 text-emerald-700" :
    v === "pending" || v === "draft" ? "bg-amber-50 text-amber-700" :
    v === "cancelled" ? "bg-red-50 text-red-700" :
    v === "invoiced" ? "bg-blue-50 text-blue-700" :
    v === "loading" ? "bg-blue-50 text-blue-700" :
    v === "departed" ? "bg-purple-50 text-purple-700" :
    v === "arrived" ? "bg-slate-100 text-slate-600" :
    "bg-gray-100 text-gray-700";
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium capitalize ${s}`}>{v}</span>;
};

export default Dashboard;